import { Container, Application } from "pixi.js";

import type { IStudioWidget } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import { EStudioWidgetType } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

import StrategyBounds from "./StrategyBounds";

import DrawCache from "./DrawCache";

import { TimerWidgetRenderer } from "./Widgets/TimerWidget";

import { CountdownWidgetRenderer } from "./Widgets/CountDownWidget/CountdownWidget";

import { DateTimeWidgetRenderer } from "./Widgets/DateTimeWidget";

import { CrawlWidgetRenderer } from "./Widgets/CrawlWidget";

import { QRWidgetRenderer } from "./Widgets/QRWidget";

import { SocialsWidgetRenderer } from "./Widgets/SocialsWidget/SocialsWidget";

import { AudioVisualizerWidgetRenderer } from "./Widgets/AudioVisualizerWidget";

import { SubGoalWidgetRenderer } from "./Widgets/SubGoalWidget";

import { FollowerGoalWidgetRenderer } from "./Widgets/FollowerGoalWidget";

import { ViewersWidgetRenderer } from "./Widgets/ViewersWidget";

import { ChatWidgetRenderer } from "./Widgets/ChatWidget";

import { ProgressBarWidgetRenderer } from "./Widgets/ProgressBarWidget";

import { PlaylistWidgetRenderer } from "./Widgets/PlaylistWidget";

import { LiveCaptionsWidgetRenderer } from "./Widgets/LiveCaptionsWidget";

import { WeatherWidgetRenderer } from "./Widgets/WeatherWidget";

import LiveCaptionsService from "../../captions/LiveCaptionsService";

import type { IStudioWidgetLiveCaptions } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Widgets.interface";

class WidgetsFactory {
  private Root: Container;

  private DrawCache: DrawCache;

  // Widget renderers

  private timerRenderer = new TimerWidgetRenderer();

  private countdownRenderer = new CountdownWidgetRenderer();

  private dateTimeRenderer = new DateTimeWidgetRenderer();

  private crawlRenderer = new CrawlWidgetRenderer();

  private qrRenderer = new QRWidgetRenderer();

  private socialsRenderer = new SocialsWidgetRenderer();

  private audioVisualizerRenderer = new AudioVisualizerWidgetRenderer();

  private subGoalRenderer = new SubGoalWidgetRenderer();

  private followerGoalRenderer = new FollowerGoalWidgetRenderer();

  private viewersRenderer = new ViewersWidgetRenderer();

  private chatRenderer = new ChatWidgetRenderer();

  private progressBarRenderer = new ProgressBarWidgetRenderer();

  private playlistRenderer = new PlaylistWidgetRenderer();

  private liveCaptionsRenderer = new LiveCaptionsWidgetRenderer();

  private weatherRenderer = new WeatherWidgetRenderer();

  private Options = {
    width: 1920,

    height: 1080,
  };

  constructor(_app: Application, drawCache: DrawCache, root: Container) {
    this.Root = root;

    this.DrawCache = drawCache;
  }

  async renderWidgets(widgets: IStudioWidget[]) {
    // Toggle the ASR session based on the LIVE_CAPTIONS widget's `active`

    // flag. Done with the full list so deactivation is observed (the loop

    // below only visits active widgets).

    this.reconcileLiveCaptions(widgets);

    for (const widget of widgets) {
      if (!widget.active) continue;

      const widgetKey = `widget_${widget.id}`;

      const sizeWidget = StrategyBounds.getBounds({
        bound: widget.bounds,

        container: {
          width: this.Options.width,

          height: this.Options.height,
        },
      });

      const destination = {
        x: sizeWidget.left,

        y: sizeWidget.top,

        width: sizeWidget.width,

        height: sizeWidget.height,
      };

      // Properly tear down the previous container BEFORE creating a new

      // one. Earlier code only called `removeChildren()` and overwrote the

      // local variable — leaving the old Container parented to

      // `App.stage`. Each re-render leaked a Container; the chat widget

      // (which sets `container.mask = ...`) compounded the problem because

      // orphan masks corrupted the render state and blacked out the canvas.

      const previousContainer = this.DrawCache.Containers[widgetKey];

      if (previousContainer) {
        previousContainer.removeFromParent();

        previousContainer.destroy({ children: true });
      }

      const widgetContainer = new Container();

      widgetContainer.label = widgetKey;

      this.DrawCache.Containers[widgetKey] = widgetContainer;

      this.Root.addChild(widgetContainer);

      widgetContainer.zIndex = widget.bounds.zIndex;

      widgetContainer.position.set(destination.x, destination.y);

      try {
        await this.renderWidgetContent(widget, widgetContainer, destination);
      } catch (err) {
        // Resiliencia: un widget que falla NO debe tumbar todo el pase de render

        // (antes se re-lanzaba y rompía la preview ENTERA de la escena). Se loguea

        // y se sigue con el resto de los widgets.

        console.error(
          `[WidgetsFactory] renderWidgetContent THREW for type=${widget.type} id=${widget.id}:`,

          err,
        );

        continue;
      }

      // Cache static widgets as texture so PixiJS skips re-rendering each tick.

      // Dynamic widgets (timer, countdown, datetime, crawl, audio visualizer) update

      // their content every tick and must NOT be cached.

      const isStaticWidget =
        widget.type === EStudioWidgetType.QR_CODE ||
        widget.type === EStudioWidgetType.SOCIALS;

      // Progress bar is always time-driven now (manual mode removed) so it

      // must NOT be cached as a texture — TimerService ticks would never

      // reach the canvas after the first frame.

      if (isStaticWidget) {
        // cacheAsTexture puede tirar si la textura del sprite aún no está subida a

        // GPU (el QR usa Texture.from(new Image()), no Assets.load como SOCIALS).

        // Si el cacheo falla, el widget igual renderiza en vivo (sin cache).

        try {
          widgetContainer.cacheAsTexture(true);
        } catch (err) {
          console.error(
            `[WidgetsFactory] cacheAsTexture falló para type=${widget.type} id=${widget.id}:`,

            err,
          );
        }
      }

      this.Root.sortDirty = true;
    }
  }

  private async renderWidgetContent(
    widget: IStudioWidget,

    container: Container,

    destination: { x: number; y: number; width: number; height: number },
  ) {
    switch (widget.type) {
      case EStudioWidgetType.TIMER:
        this.timerRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.COUNTDOWN:
        this.countdownRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.DATE_TIME:
        this.dateTimeRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.CRAWL:
        this.crawlRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.QR_CODE:
        await this.qrRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.SOCIALS:
        await this.socialsRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.AUDIO_VISUALIZER:
        this.audioVisualizerRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.SUB_GOAL:
        this.subGoalRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.FOLLOWER_GOAL:
        this.followerGoalRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.VIEWERS:
        this.viewersRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.CHAT:
        this.chatRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.PROGRESS_BAR:
        this.progressBarRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.PLAYLIST:
        this.playlistRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.LIVE_CAPTIONS:
        this.liveCaptionsRenderer.render(widget, container, destination);

        break;

      case EStudioWidgetType.WEATHER:
        this.weatherRenderer.render(widget, container, destination);

        break;
    }
  }

  /**
   * Drive the (single, program-wide) ASR session from the LIVE_CAPTIONS
   * widgets' `active` flag. Called once per `renderWidgets` pass with the
   * FULL widget list (not just the active ones) so we observe the
   * active→inactive transition too — `renderWidgetContent` only ever sees
   * active widgets, so it can't stop the service on its own.
   */

  private reconcileLiveCaptions(widgets: IStudioWidget[]) {
    const active = widgets.find(
      (w) => w.type === EStudioWidgetType.LIVE_CAPTIONS && w.active,
    ) as IStudioWidgetLiveCaptions | undefined;

    const service = LiveCaptionsService.getInstance();

    if (active) {
      void service.start({
        engine: active.options.engine ?? "faster-whisper",

        lang: active.options.lang ?? null,

        task: active.options.task ?? "transcribe",

        maxLines: active.options.maxLines ?? 2,
      });
    } else if (service.isRunning()) {
      service.stop();
    }
  }

  destroy() {
    // Clean up widget renderers first (this unsubscribes from services like TimerService)

    this.timerRenderer.destroy();

    this.countdownRenderer.destroy();

    this.dateTimeRenderer.destroy();

    this.crawlRenderer.destroy();

    this.qrRenderer.destroy();

    this.socialsRenderer.destroy();

    this.audioVisualizerRenderer.destroy();

    this.subGoalRenderer.destroy();

    this.followerGoalRenderer.destroy();

    this.viewersRenderer.destroy();

    this.chatRenderer.destroy();

    this.progressBarRenderer.destroy();

    this.playlistRenderer.destroy();

    this.liveCaptionsRenderer.destroy();

    this.weatherRenderer.destroy();

    // The factory is being torn down (e.g. scene unmount) — stop the ASR

    // session too so the sidecar isn't left running on a dead canvas.

    try {
      LiveCaptionsService.getInstance().stop();
    } catch {
      /* best-effort */
    }

    // Clean up all widget containers

    for (const key in this.DrawCache.Containers) {
      if (key.startsWith("widget_")) {
        const container = this.DrawCache.Containers[key];

        if (container && !container.destroyed) {
          container.removeChildren();

          container.destroy();
        }

        delete this.DrawCache.Containers[key];
      }
    }
  }
}

export default WidgetsFactory;
